import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { Eyebrow, Btn } from "../components/Primitives";

const MAX_POLLS = 60;

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const checkoutId = params.get("session_id");
    const [state, setState] = useState("verifying"); // verifying | paid | failed | timeout
    const [memberNumber, setMemberNumber] = useState(null);
    const polls = useRef(0);

    useEffect(() => {
        if (!checkoutId) {
            setState("failed");
            return;
        }
        let stopped = false;
        const poll = async () => {
            if (stopped) return;
            polls.current += 1;
            try {
                const res = await api.paymentStatus(checkoutId);
                if (res.payment_status === "paid") {
                    setMemberNumber(res.founding_member_number || null);
                    setState("paid");
                    return;
                }
                if (["failed", "expired"].includes(res.payment_status)) {
                    setState("failed");
                    return;
                }
            } catch {
                // transient — keep polling
            }
            if (polls.current >= MAX_POLLS) {
                setState("timeout");
                return;
            }
            setTimeout(poll, 2000);
        };
        poll();
        return () => { stopped = true; };
    }, [checkoutId]);

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-cream px-7 text-center" data-testid="payment-success-screen">
            {state === "verifying" && (
                <>
                    <motion.div
                        className="w-12 h-12 rounded-full bg-gradient-to-br from-rose to-gold"
                        animate={{ scale: [1, 1.15, 1], opacity: [0.7, 1, 0.7] }}
                        transition={{ duration: 1.4, repeat: Infinity }}
                    />
                    <p className="mt-6 font-display italic text-xl text-burgundy" data-testid="payment-verifying">
                        Confirming your payment…
                    </p>
                    <p className="mt-2 text-xs tracking-[0.24em] uppercase text-ink-muted">Please don't close this page</p>
                </>
            )}
            {state === "paid" && (
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="payment-paid">
                    <Eyebrow>Payment confirmed</Eyebrow>
                    <h1 className="mt-4 font-display text-4xl sm:text-5xl text-burgundy leading-tight">
                        <span className="italic">Welcome</span> to Ever After.
                    </h1>
                    {memberNumber && (
                        <p className="mt-4 font-display italic text-lg text-ink" data-testid="founding-member-number">
                            You are Founding Member #{memberNumber} of 100.
                        </p>
                    )}
                    <p className="mt-3 text-sm text-ink-muted">
                        Your full compatibility profile is unlocked.
                    </p>
                    <div className="mt-8">
                        <Btn data-testid="payment-continue-btn" onClick={() => navigate("/")}>
                            Enter Ever After
                        </Btn>
                    </div>
                </motion.div>
            )}
            {(state === "failed" || state === "timeout") && (
                <div data-testid="payment-failed">
                    <Eyebrow>{state === "timeout" ? "Still processing" : "Payment issue"}</Eyebrow>
                    <h1 className="mt-4 font-display text-3xl text-burgundy">
                        {state === "timeout"
                            ? "Your payment is taking longer than expected."
                            : "We couldn't confirm this payment."}
                    </h1>
                    <p className="mt-3 text-sm text-ink-muted max-w-sm mx-auto">
                        {state === "timeout"
                            ? "If you were charged, your access will unlock automatically. Check back in a few minutes."
                            : "No charge was completed. You can return and try again."}
                    </p>
                    <div className="mt-8">
                        <Btn data-testid="payment-back-btn" onClick={() => navigate("/")}>
                            Return to Ever After
                        </Btn>
                    </div>
                </div>
            )}
        </div>
    );
}
